import { getSkillById } from '../../content/skills/skills';
import type { PlayerAttributes, PlayerState } from '../game/types';

export const EXP_PER_LEVEL_BASE = 40;
export const STAT_POINTS_PER_LEVEL = 2;

export function expToNextLevel(level: number): number {
  return EXP_PER_LEVEL_BASE + (level - 1) * 15;
}

export type GrantExpResult = {
  player: PlayerState;
  levelsGained: number;
  messages: string[];
};

/** Grant EXP; multiple level-ups are allowed. */
export function grantExp(player: PlayerState, amount: number): GrantExpResult {
  if (amount <= 0 || !player.alive) {
    return { player, levelsGained: 0, messages: [] };
  }

  let next = { ...player, exp: player.exp + amount };
  let levelsGained = 0;
  const messages: string[] = [`+${amount} EXP`];
  while (next.exp >= next.expToNext) {
    next = {
      ...next,
      exp: next.exp - next.expToNext,
      level: next.level + 1,
      expToNext: expToNextLevel(next.level + 1),
      statPoints: next.statPoints + STAT_POINTS_PER_LEVEL,
      maxMana: next.maxMana + 2,
      mana: Math.min(next.mana + 2, next.maxMana + 2),
    };
    levelsGained += 1;
    messages.push(`Lên cấp ${next.level}! +${STAT_POINTS_PER_LEVEL} điểm chỉ số.`);
  }
  return { player: next, levelsGained, messages };
}

export type StatKey = keyof PlayerAttributes;

export function allocateStat(player: PlayerState, stat: StatKey): PlayerState | null {
  if (player.statPoints <= 0) return null;
  const stats = { ...player.stats, [stat]: player.stats[stat] + 1 };
  const intelligenceGain = stat === 'intelligence' ? 3 : 0;
  const maxMana = player.maxMana + intelligenceGain;
  return {
    ...player,
    stats,
    statPoints: player.statPoints - 1,
    maxMana,
    mana: Math.min(player.mana + intelligenceGain, maxMana),
  };
}

export type LearnSkillResult =
  | { ok: false; reason: 'unknown' | 'known' | 'wrong-faction'; message: string }
  | { ok: true; player: PlayerState; skillName: string };

/** Skills may only be learned by a character of the same faction. */
export function learnSkill(player: PlayerState, skillId: string): LearnSkillResult {
  const skill = getSkillById(skillId);
  if (!skill) return { ok: false, reason: 'unknown', message: 'Kỹ năng không tồn tại.' };
  if (player.skillIds.includes(skillId)) {
    return { ok: false, reason: 'known', message: `Bạn đã biết ${skill.name}.` };
  }
  if (skill.faction !== player.faction) {
    return {
      ok: false,
      reason: 'wrong-faction',
      message: `${skill.name} thuộc phe ${skill.faction}; bạn không thể lĩnh hội.`,
    };
  }
  return {
    ok: true,
    player: { ...player, skillIds: [...player.skillIds, skillId] },
    skillName: skill.name,
  };
}
